import type { BackofficeOrganizationFeatures } from '../types';

const FEATURE_ROWS: Array<{
  key: keyof BackofficeOrganizationFeatures;
  label: string;
  summary: string;
}> = [
  {
    key: 'insights',
    label: 'Nava Insights',
    summary: 'Turn off for a customer that only has Nava Customer Success.',
  },
  {
    key: 'organizations',
    label: 'Organizations members',
    summary: 'Organization people, invitations, roles, and access management.',
  },
  {
    key: 'sso',
    label: 'Single sign-on',
    summary:
      'Company OIDC or SAML sign-in. Requires Organizations members; enabling SSO also enables it.',
  },
  {
    key: 'domains',
    label: 'Domains',
    summary: 'Verified domains, account claims, and domain-based organization access.',
  },
  {
    key: 'integrations',
    label: 'Integrations',
    summary: 'Organization-level integrations and connected external systems.',
  },
  {
    key: 'apikeys',
    label: 'API keys',
    summary: 'Organization API key creation and management.',
  },
  {
    key: 'bringYourOwnParticipants',
    label: 'Bring your own participants',
    summary: 'Participant uploads, personal interview links, and reusable study lists.',
  },
  {
    key: 'customParticipantPrivacy',
    label: 'Custom participant privacy',
    summary: 'Study-specific privacy descriptions and policy links before consent.',
  },
  {
    key: 'interviewRecordingPrivacy',
    label: 'Interview recording privacy',
    summary: 'Transparent, obfuscated, or hidden interview recording playback settings.',
  },
  {
    key: 'collectParticipantEmail',
    label: 'Collect participant email',
    summary: 'Finished-screen email collection after the interview is completed.',
  },
  {
    key: 'workspaces',
    label: 'Workspaces',
    summary:
      'Cross-study workspaces: combine studies and documents, synthesis, and workspace chat.',
  },
  {
    key: 'customerListening',
    label: 'Customer listening',
    summary:
      'Customer Success solution: accounts, recurring AI check-ins, signals, and write-back.',
  },
  {
    key: 'outboundPhoneCalls',
    label: 'Outbound phone calls',
    summary: 'Call study participants by phone using the published interview agent.',
  },
  {
    key: 'customerListeningSms',
    label: 'Customer listening SMS',
    summary: 'SMS check-in invitations and reminders to Swedish mobile numbers.',
  },
];

export function featuresEqual(
  left: BackofficeOrganizationFeatures,
  right: BackofficeOrganizationFeatures
): boolean {
  return FEATURE_ROWS.every((feature) => left[feature.key] === right[feature.key]);
}

interface OrganizationFeatureFieldsProps {
  features: BackofficeOrganizationFeatures;
  defaults?: BackofficeOrganizationFeatures | undefined;
  disabled?: boolean;
  onChange: (features: BackofficeOrganizationFeatures) => void;
  /**
   * The Customer Success contact allowance of the plan, edited beside the
   * Customer listening switch. Only the editor of an existing organization
   * passes it; null means no allowance is set.
   */
  contactLimit?:
    | {
        value: number | null;
        onChange: (value: number | null) => void;
      }
    | undefined;
}

export function OrganizationFeatureFields({
  features,
  defaults,
  disabled = false,
  onChange,
  contactLimit,
}: OrganizationFeatureFieldsProps) {
  return (
    <div className="divide-y divide-neutral-100">
      {FEATURE_ROWS.map((feature) => {
        const enabled = features[feature.key] === true;
        const differs = defaults !== undefined && enabled !== defaults[feature.key];
        return (
          <div
            key={feature.key}
            className={[
              'grid gap-3 px-5 py-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-center',
              feature.key === 'sso' ? 'ml-5 border-l border-neutral-200' : '',
              differs ? 'bg-amber-50' : 'hover:bg-neutral-50',
            ].join(' ')}
          >
            <span>
              <span className="block text-body font-semibold text-neutral-900">
                {feature.label}
              </span>
              <span className="mt-1 block text-small text-neutral-500">{feature.summary}</span>
              {differs ? (
                <span className="mt-1 block text-small font-medium text-amber-800">
                  Differs from tier default: {defaults[feature.key] ? 'Enabled' : 'Disabled'}
                </span>
              ) : null}
              {feature.key === 'customerListening' && contactLimit ? (
                <label className="mt-3 block max-w-xs">
                  <span className="text-small font-medium text-neutral-700">Contact allowance</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={contactLimit.value ?? ''}
                    onChange={(event) => {
                      const value = event.target.value.trim();
                      contactLimit.onChange(value ? Number(value) : null);
                    }}
                    disabled={disabled}
                    className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-body outline-none focus:border-neutral-900"
                    placeholder="No allowance"
                  />
                  <span className="mt-1 block text-small text-neutral-500">
                    Customer Success contacts the plan includes. Leave it empty for no allowance.
                  </span>
                </label>
              ) : null}
            </span>
            <span className="inline-flex items-center gap-3">
              <span
                className={[
                  'rounded-full px-2.5 py-1 text-caption font-semibold',
                  enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-100 text-neutral-600',
                ].join(' ')}
              >
                {enabled ? 'Enabled' : 'Disabled'}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label={feature.label}
                className={[
                  'focus-ring relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                  enabled ? 'bg-backoffice-500' : 'bg-neutral-300',
                ].join(' ')}
                onClick={() => {
                  const next = { ...features, [feature.key]: !enabled };
                  if (feature.key === 'sso' && !enabled) next.organizations = true;
                  if (feature.key === 'organizations' && enabled) next.sso = false;
                  onChange(next);
                }}
                disabled={disabled}
              >
                <span
                  className={[
                    'inline-block size-5 rounded-full bg-white shadow-sm transition-transform',
                    enabled ? 'translate-x-5' : 'translate-x-0',
                  ].join(' ')}
                  aria-hidden="true"
                />
              </button>
            </span>
          </div>
        );
      })}
    </div>
  );
}
